/** 编辑器状态允许的最小时间轴比例。 */
export const MIN_TIMELINE_RATIO = 0.3;
/** 编辑器状态允许的最大时间轴比例。 */
export const MAX_TIMELINE_RATIO = 0.68;
/** 编辑器初始状态，保证首次打开页面拥有稳定的布局和模式。 */
export const initialEditorState = {
    targetChapterId: null,
    targetNodeId: null,
    timelineRatio: 0.38,
    isPlaying: false,
    selectedElementId: null,
    choiceTextEditRequest: null,
    choiceTextEditVersion: 0,
    canvasSize: { width: 0, height: 0 },
    timelineCursorMs: 0,
    timelineSeekVersion: 0,
    isTimelineSnappingEnabled: true,
    isVideoTrackMuted: false,
    triggerConflictMessage: null,
    nodeType: null,
    nodeStatus: 'idle',
    isNodeDirty: false,
};
/** 构造事件触发时间冲突提示动作；传 null 表示关闭提示。 */
export function setTriggerConflict(payload) {
    return { type: 'editor/setTriggerConflict', payload };
}
/** 构造当前节点加载开始动作。 */
export function nodeLoadStarted() {
    return { type: 'editor/nodeLoadStarted' };
}
/** 构造当前节点加载成功动作，携带节点类型供入口守卫判断。 */
export function nodeLoadSucceeded(nodeType) {
    return { type: 'editor/nodeLoadSucceeded', payload: { nodeType } };
}
/** 构造当前节点加载失败动作。 */
export function nodeLoadFailed() {
    return { type: 'editor/nodeLoadFailed' };
}
/** 构造当前节点提交开始动作。 */
export function nodeSaveStarted() {
    return { type: 'editor/nodeSaveStarted' };
}
/** 构造当前节点提交成功动作。 */
export function nodeSaveSucceeded() {
    return { type: 'editor/nodeSaveSucceeded' };
}
/** 构造当前节点提交失败动作；失败时保留脏标记以便重试。 */
export function nodeSaveFailed() {
    return { type: 'editor/nodeSaveFailed' };
}
/** 构造当前节点脏标记动作。 */
export function setNodeDirty(payload) {
    return { type: 'editor/setNodeDirty', payload };
}
/** 构造编辑器 URL 目标同步动作。 */
export function setEditorTarget(payload) {
    return { type: 'editor/setTarget', payload };
}
/** 将时间轴比例限制在产品约定的 30% 到 68% 范围内。 */
export function clampTimelineRatio(ratio) {
    return Math.min(MAX_TIMELINE_RATIO, Math.max(MIN_TIMELINE_RATIO, ratio));
}
/** 将游标时间限制为非负数；上限由调用方（时间轴刻度、播放引擎）依据视频排布裁决。 */
function clampCursorTimeMs(timeMs) {
    return Math.max(0, Math.round(timeMs));
}
/** 根据动作生成新的编辑器状态，保持状态转换集中且可测试。 */
export function editorReducer(state = initialEditorState, action) {
    switch (action.type) {
        case 'editor/setTarget':
            return {
                ...state,
                targetChapterId: action.payload.chapterId,
                targetNodeId: action.payload.nodeId,
            };
        case 'editor/setTimelineRatio':
            return { ...state, timelineRatio: clampTimelineRatio(action.payload) };
        case 'editor/togglePlaying':
            return { ...state, isPlaying: !state.isPlaying };
        case 'editor/stopPlaying':
            return state.isPlaying ? { ...state, isPlaying: false } : state;
        case 'editor/setSelectedElement':
            return { ...state, selectedElementId: action.payload, choiceTextEditRequest: null };
        case 'editor/editChoiceText':
            return {
                ...state,
                selectedElementId: action.payload.eventId,
                choiceTextEditVersion: state.choiceTextEditVersion + 1,
                choiceTextEditRequest: {
                    ...action.payload,
                    version: state.choiceTextEditVersion + 1,
                },
            };
        case 'editor/setCanvasSize': {
            /** 尺寸未变化时保持原引用，避免画布测量回调触发无意义的重渲染。 */
            const { width, height } = action.payload;
            if (state.canvasSize.width === width && state.canvasSize.height === height)
                return state;
            return { ...state, canvasSize: { width, height } };
        }
        case 'editor/setTimelineCursor':
            return { ...state, timelineCursorMs: clampCursorTimeMs(action.payload) };
        case 'editor/seekTimelineCursor':
            return {
                ...state,
                timelineCursorMs: clampCursorTimeMs(action.payload),
                timelineSeekVersion: state.timelineSeekVersion + 1,
            };
        case 'editor/toggleTimelineSnapping':
            return { ...state, isTimelineSnappingEnabled: !state.isTimelineSnappingEnabled };
        case 'editor/toggleVideoTrackMuted':
            return { ...state, isVideoTrackMuted: !state.isVideoTrackMuted };
        case 'editor/setTriggerConflict':
            return state.triggerConflictMessage === action.payload
                ? state
                : { ...state, triggerConflictMessage: action.payload };
        case 'editor/nodeLoadStarted':
            /** 切换编辑目标时先清空上一个节点的类型，避免守卫读到旧值。 */
            return {
                ...state,
                nodeType: null,
                nodeStatus: 'loading',
                isNodeDirty: false,
            };
        case 'editor/nodeLoadSucceeded':
            return {
                ...state,
                nodeType: action.payload.nodeType,
                nodeStatus: 'ready',
                isNodeDirty: false,
            };
        case 'editor/nodeLoadFailed':
            return {
                ...state,
                nodeType: null,
                nodeStatus: 'error',
                isNodeDirty: false,
            };
        case 'editor/nodeSaveStarted':
            return { ...state, nodeStatus: 'saving' };
        case 'editor/nodeSaveSucceeded':
            return { ...state, nodeStatus: 'ready', isNodeDirty: false };
        case 'editor/nodeSaveFailed':
            /** 保存失败必须保留本地修改，便于调用方提供重试入口。 */
            return {
                ...state,
                nodeStatus: 'error',
                isNodeDirty: true,
            };
        case 'editor/setNodeDirty':
            return state.isNodeDirty === action.payload
                ? state
                : { ...state, isNodeDirty: action.payload };
        default:
            return state;
    }
}
